'use client';

import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {ManagedShippingProps} from '@/molecules/Fragments/Shipping/ManagedShipping/typings';
import type Props from './typings';

import ShippingProduct from '@/molecules/Fragments/Pages/Product/Shipping/ShippingProductPage';
import ManagedShipping from '@/molecules/Fragments/Shipping/ManagedShipping/ManagedShipping';
import {clearShippingInfo, getShippingInfo} from '@/redux/actions/productActions';
import {selectProductData} from '@/redux/reducers/productReducer';
import {selectUserSettingsData} from '@/redux/reducers/userSettingsReducer';

const isObjectEmpty = (value?: Record<string, any> | null) => {
  if (!value) return true;
  return Object.keys(value).length === 0;
};

const Shipping = ({
  isManagedShipping,
  productType,
  selectedProduct,
  artistName,
  artworkName,
  categorySlug,
  currency,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const customerProductData = useSelector(selectProductData);

  const isCurrentShippingDataEmpty =
    productType === 'original'
      ? isObjectEmpty(customerProductData.shipping.original)
      : isObjectEmpty(customerProductData.shipping.prints[selectedProduct?.slug ?? '']);

  /**
   * Effect to fetch shipping data:
   * - if managed shipping is enabled
   * - on the product selected
   * - if shipping data has not been fetched yet.
   */
  useEffect(() => {
    if (isManagedShipping && selectedProduct?.slug && isCurrentShippingDataEmpty) {
      dispatch(
        getShippingInfo({
          slug: selectedProduct?.slug,
          type: productType,
          successCallbacks: [],
          errorCallbacks: [],
          cleanupCallbacks: [],
        }),
      );
    }
  }, [isManagedShipping, selectedProduct?.slug, isCurrentShippingDataEmpty]);

  return (
    <>
      {!isManagedShipping && (
        <ShippingProduct
          productSlug={selectedProduct.slug}
          productType={productType}
          artistName={artistName}
          artworkName={artworkName}
          reduxActions={{get: getShippingInfo, clear: clearShippingInfo}}
          reduxStates={{product: selectProductData, settings: selectUserSettingsData}}
        />
      )}

      <ManagedShipping
        selectedProduct={selectedProduct as ManagedShippingProps['selectedProduct']}
        selectedCurrency={currency}
        categorySlug={categorySlug}
        initialShippingInfo={customerProductData.shipping}
        productType={productType}
        isEnabled={isManagedShipping}
      />
    </>
  );
};

export default Shipping;
